import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import momentTz from 'moment-timezone';

import { TIMEZONE_MAP } from '@/utils/date';

// Order refill cases are worked in on My Cases' Pending tab (JNJ-279). The
// rank is the bucket from the PSS daily call log; lower is worked first:
//
//   1 offline handoff (the AI reached the patient and handed off)
//   2 0 attempts, due in 0–5 days
//   3 0 attempts, due in 6–8 days
//   4 1 attempt, Nimble opt-out
//   5 2 attempts, Nimble opt-out, last call 5+ business days ago
//   6 0 attempts, due in 9–10 days
//   7 0 attempts, overdue 1–90 days
//   8 1 attempt, Nimble opt-in (filler)
//   9 everything else
//
// Within a bucket: refill due date, soonest first, cases without one last.
// "Today" and business days are ET calendar days.

const MS_PER_DAY = 86_400_000;
const ET = TIMEZONE_MAP.ET;
const OTHER = 9;

// Calendar day as a day count, so day differences ignore DST.
const dayNumber = (year: number, month: number, day: number) =>
  Date.UTC(year, month, day) / MS_PER_DAY;

const etDayNumber = (instant: momentTz.MomentInput): number => {
  const m = momentTz.tz(instant, ET);
  return dayNumber(m.year(), m.month(), m.date());
};

// refill_date is MM/DD/YYYY, as intake and jnj-flows write it.
const dueDayNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'string') return undefined;
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.trim());
  if (!match) return undefined;
  return dayNumber(Number(match[3]), Number(match[1]) - 1, Number(match[2]));
};

// Weekdays after `from` up to and including `to`. Holidays are not skipped,
// matching the manual process.
const businessDaysBetween = (from: number, to: number): number => {
  let count = 0;
  for (let d = from + 1; d <= to; d++) {
    const weekday = new Date(d * MS_PER_DAY).getUTCDay();
    if (weekday !== 0 && weekday !== 6) count++;
  }
  return count;
};

// 0-attempt cases by days until due: [from, to, rank].
const ZERO_ATTEMPT_WINDOWS: [number, number, number][] = [
  [0, 5, 2],
  [6, 8, 3],
  [9, 10, 6],
  [-90, -1, 7],
];

const zeroAttemptRank = (daysToDue: number | undefined): number => {
  if (daysToDue === undefined) return OTHER;
  const window = ZERO_ATTEMPT_WINDOWS.find(([from, to]) => daysToDue >= from && daysToDue <= to);
  return window ? window[2] : OTHER;
};

const followUpRank = (
  c: GetCaseRecordResponse,
  attempts: number,
  optedIn: boolean,
  today: number,
): number => {
  if (attempts === 1) return optedIn ? 8 : 4;
  if (attempts !== 2 || optedIn || !c.last_human_call_at) return OTHER;
  return businessDaysBetween(etDayNumber(c.last_human_call_at), today) >= 5 ? 5 : OTHER;
};

export const refillPriority = (
  c: GetCaseRecordResponse,
  now: Date,
): { rank: number; due?: number } => {
  const data = (c.input_data?.data as Record<string, unknown>) || {};
  const due = dueDayNumber(data.refill_date);
  if ((c.ai_call_attempts ?? 0) > 0) return { rank: 1, due };

  const today = etDayNumber(now);
  const attempts = c.human_call_attempts ?? 0;
  if (attempts === 0) {
    return { rank: zeroAttemptRank(due === undefined ? undefined : due - today), due };
  }
  // Not in the schema yet; until it is, every patient counts as opted out.
  const optedIn = data.nimble_opt_out === 'no';
  return { rank: followUpRank(c, attempts, optedIn, today), due };
};

export const compareRefillPriority = (
  a: GetCaseRecordResponse,
  b: GetCaseRecordResponse,
  now: Date,
): number => {
  const pa = refillPriority(a, now);
  const pb = refillPriority(b, now);
  if (pa.rank !== pb.rank) return pa.rank - pb.rank;
  if (pa.due !== pb.due) {
    if (pa.due === undefined) return 1;
    if (pb.due === undefined) return -1;
    return pa.due - pb.due;
  }
  return (a.id ?? '').localeCompare(b.id ?? '');
};
